import { useCallback } from "react";
import {
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
  FieldErrors,
} from "react-hook-form";
import { FileText } from "lucide-react";
import { FormField, Input, Select } from "../UI/FormField";
import { SectionCard } from "../UI/SectionCard";
import { DirectorySelect } from "../directory/DirectorySelect";
import type { EntitySnapshot } from "../../types";
import type { ContainerOrderFormData } from "../../types/containerOrders";
import { INCOTERM_OPTIONS } from "../../constants/taskConstants";
import { useTranslation } from "react-i18next";

interface GeneralInfoSectionProps {
  register: UseFormRegister<ContainerOrderFormData>;
  setValue: UseFormSetValue<ContainerOrderFormData>;
  watch: UseFormWatch<ContainerOrderFormData>;
  errors: FieldErrors<ContainerOrderFormData>;
  readonly: boolean;
  getErrorMessage: (error: any) => string | undefined;
}

/**
 * Section 1 — general information.
 *
 * Six rows, matching the mockup: Reference, Contract, Client, Direction,
 * Incoterm / Place, Date range.
 *
 * The client shows a single row, but picking a company from the Partners
 * directory still captures its full snapshot (registration code, phone, email,
 * address) into form state behind the scenes — those values ride along to the
 * generated shipments without cluttering the order form.
 *
 * Unlike the shipment form, the client here must be a directory record: an
 * order is the template a whole batch of shipments (and their invoices) is
 * generated from, so a free-typed name would multiply one typo across the
 * batch. DirectorySelect enforces that — see the note on the component.
 */
export function GeneralInfoSection({
  register,
  setValue,
  watch,
  errors,
  readonly,
  getErrorMessage,
}: GeneralInfoSectionProps) {
  const { t } = useTranslation("containerOrders");

  const clientCompanyName = watch("client.companyName");
  const clientPartnerId = watch("client.partnerId");

  const handleClientSelect = useCallback(
    (snapshot: EntitySnapshot) => {
      const country = snapshot?.address?.country;
      // The link is the value the form validates on, so set it with
      // `shouldValidate` — it has no registered input to re-validate itself.
      setValue("client.partnerId", snapshot._id ?? null, {
        shouldValidate: true,
      });
      setValue("client.companyName", snapshot.companyName);
      setValue("client.registerationCode", snapshot.registerationCode || "");
      setValue("client.phoneNumber", snapshot.phoneNumber || "");
      setValue("client.emailAddress", snapshot.emailAddress || "");
      setValue("client.address.street", snapshot.address?.street || "");
      setValue("client.address.city", snapshot.address?.city || "");
      setValue("client.address.state", snapshot.address?.state || "");
      setValue("client.address.postalCode", snapshot.address?.postalCode || "");
      setValue(
        "client.address.country",
        typeof country === "object" && country !== null ? country._id : country,
      );
      // Carried through when the partner record has one. Unlike the three route
      // stops, the client address is a billing/reference address rather than a
      // dispatch point, so a missing location is not an error here — it simply
      // is not stored.
      setValue(
        "client.address.coordinates",
        snapshot.address?.coordinates ?? undefined,
      );
    },
    [setValue],
  );

  const handleClientClear = useCallback(() => {
    setValue("client.partnerId", null);
    setValue("client.companyName", "");
    setValue("client.registerationCode", "");
    setValue("client.phoneNumber", "");
    setValue("client.emailAddress", "");
    setValue("client.address.street", "");
    setValue("client.address.city", "");
    setValue("client.address.state", "");
    setValue("client.address.postalCode", "");
    setValue("client.address.country", "");
    setValue("client.address.coordinates", undefined);
  }, [setValue]);

  return (
    <SectionCard icon={FileText} title={t("sections.generalInformation")}>
      <div className="space-y-1">
        <FormField
          layout="inline"
          size="sm"
          label={t("labels.reference")}
          error={getErrorMessage(errors.referenceNumber)}
        >
          <Input
            {...register("referenceNumber")}
            size="sm"
            readOnly={readonly}
            // Mono for the value it holds — a code, read character by
            // character — but the placeholder is a sentence, so it keeps the
            // form's own font, same as Shipping line.
            className="font-mono placeholder:font-sans"
            // Both are free text with no house format, so the hint has to say
            // what belongs here; an empty box next to "Reference" reads as a
            // system-assigned number waiting to appear.
            placeholder={readonly ? undefined : t("placeholders.reference")}
          />
        </FormField>

        <FormField
          layout="inline"
          size="sm"
          // "Booking number" — the carrier's booking for this order. The field
          // behind it is still `contractNumber`; see the model.
          label={t("labels.bookingNumber")}
          error={getErrorMessage(errors.contractNumber)}
        >
          <Input
            {...register("contractNumber")}
            size="sm"
            readOnly={readonly}
            className="font-mono placeholder:font-sans"
            placeholder={readonly ? undefined : t("placeholders.bookingNumber")}
          />
        </FormField>

        <FormField
          layout="inline"
          size="sm"
          label={t("labels.client")}
          required
          // The missing-link message is the actionable one — "pick from the
          // directory" — so it wins over the plain "client is required" that
          // fires alongside it on an empty field.
          error={
            getErrorMessage(errors.client?.partnerId) ??
            getErrorMessage(errors.client?.companyName)
          }
        >
          <DirectorySelect
            source="partners"
            selectedId={clientPartnerId}
            value={clientCompanyName || ""}
            onSelect={handleClientSelect}
            onClear={handleClientClear}
            disabled={readonly}
            size="sm"
            placeholder={t("placeholders.client")}
            error={!!errors.client?.partnerId || !!errors.client?.companyName}
          />
        </FormField>

        {/*
          Direction is no longer asked for.

          The field is still on the order and is still read — `legOf` in
          utils/containerCmr.ts uses it to decide which leg of the run carries
          the goods on the consignment note, and orders saved with a direction
          keep it and keep printing correctly. What has gone is the question:
          new orders take the model's default, and nobody has to answer it.

          Deliberately removed from the form rather than from the record. A
          container's CMR has to name a consignor and a consignee, and without a
          direction `legOf` falls back to reading the run end to end — which
          prints the itinerary rather than the consignment, on a document
          somebody signs.
        */}

        {/* Incoterm + named place — one row, two inputs, as in the mockup. */}
        <FormField
          layout="inline"
          size="sm"
          label={t("labels.incotermPlace")}
          error={
            getErrorMessage(errors.incoterm) ?? getErrorMessage(errors.place)
          }
        >
          {/* Two controls share this row only while the row is wide enough for
              both; below that they stack. Measured against the control's own
              width (@container/control on FormField), not the viewport. */}
          <div className="grid grid-cols-1 gap-2 @2xs/control:grid-cols-2">
            <Select
              {...register("incoterm")}
              options={INCOTERM_OPTIONS.map((code) => ({
                value: code,
                label: code,
              }))}
              disabled={readonly}
              size="sm"
              placeholder={t("placeholders.incoterm")}
              error={!!errors.incoterm}
            />
            <Input
              {...register("place")}
              size="sm"
              readOnly={readonly}
              placeholder={t("placeholders.place")}
            />
          </div>
        </FormField>

        {/* Validity window for the batch. */}
        <FormField
          layout="inline"
          size="sm"
          label={t("labels.dateRange")}
          error={
            getErrorMessage(errors.dateFrom) ?? getErrorMessage(errors.dateTo)
          }
        >
          <div className="grid grid-cols-1 gap-2 @2xs/control:grid-cols-2">
            <Input
              {...register("dateFrom")}
              type="date"
              size="sm"
              readOnly={readonly}
              error={!!errors.dateFrom}
            />
            <Input
              {...register("dateTo")}
              type="date"
              size="sm"
              readOnly={readonly}
              error={!!errors.dateTo}
            />
          </div>
        </FormField>
      </div>
    </SectionCard>
  );
}
